import * as React from 'react'
import Link from 'next/link'
import serializeObject from '@arsenal/arsenal/modules/serializeObject'
import classNames from 'classnames'
import styles from './common-link.module.scss'
export enum ECommonLinkType {
  SERIES,
  SCORE,
  DEALER,
  CONFIG,
  COMMUNITY,
  COMMUNITY_QA,
  USED_CAR,
  BRAND,
  IMAGE_LIST,
  WG_IMAGE = 'wg',
  NS_IMAGE = 'ns',
  KJ_IMAGE = 'kj',
  TEST = 'test',
}
export interface ICommonLinkProps {
  id?: number | string
  href?: string
  className?: string
  style?: React.CSSProperties
  target?: '_blank' | '_self'
  children?: React.ReactNode
  type: ECommonLinkType
  rel?: string
  query?: Record<string, string | number | undefined>
  title?: string
  disabled?: boolean
  onClick?(e: React.MouseEvent): void
}

const NONE_HREF = undefined
export const createHref = ({
  id,
  type,
  query = {},
}: Pick<ICommonLinkProps, 'id' | 'type' | 'query'>): string | undefined => {
  const suffix = Object.keys(query).length ? `?${serializeObject(query)}` : ''
  switch (type) {
    case ECommonLinkType.SERIES:
      return id ? `/auto/series/${id}${suffix}` : NONE_HREF
    case ECommonLinkType.SCORE:
      return id ? `/auto/series/score/${id}${suffix}` : NONE_HREF
    case ECommonLinkType.DEALER:
      return id ? `/dealer/${id}${suffix}` : NONE_HREF
    case ECommonLinkType.CONFIG:
      return id ? `/auto/params-carIds-x-${id}${suffix}` : NONE_HREF
    case ECommonLinkType.COMMUNITY:
      return id ? `/community/${id}${suffix}` : NONE_HREF
    case ECommonLinkType.COMMUNITY_QA:
      return id ? `/community/${id}/wenda${suffix}` : NONE_HREF
    case ECommonLinkType.BRAND:
      return id
        ? `/auto/library/x-x-x-x-x-x-x-x-x-x-x-x-x-x-x-${id}-x`
        : NONE_HREF
    case ECommonLinkType.IMAGE_LIST:
      return id ? `/auto/series/${id}/images-wg${suffix}` : NONE_HREF
    case ECommonLinkType.WG_IMAGE:
    case ECommonLinkType.NS_IMAGE:
    case ECommonLinkType.KJ_IMAGE:
      return id ? `/series-${id}/images/${type}-x-x-x` : NONE_HREF
    case ECommonLinkType.TEST:
      return id ? `/test/${id}` : NONE_HREF
    default:
      return NONE_HREF
  }
}
export default function CommonLink({
  id,
  target = '_blank',
  className,
  style,
  children,
  type,
  href,
  rel,
  title,
  query,
  disabled,
  onClick,
}: ICommonLinkProps) {
  // 根据不同类型，计算跳转链接。自定义链接优先级高于默认计算规则
  const computedHref = React.useMemo(() => {
    if (disabled) return NONE_HREF
    return href || createHref({ id, type, query })
  }, [id, type, href, query, disabled])

  // 链接禁用状态。自定义禁用状态优先级高于计算后无链接的禁用状态
  const computedDisabled = React.useMemo(() => {
    if (disabled !== undefined) return disabled
    return computedHref === NONE_HREF
  }, [computedHref, disabled])

  // 点击事件，禁用时不派发onClick
  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (computedDisabled) {
      event.preventDefault()
      return
    }
    onClick?.(event)
  }
  const anchor = (
    <a
      className={classNames(className, styles.root, {
        [styles.disabled]: computedDisabled,
        disabled: computedDisabled,
      })}
      style={style}
      target={target}
      href={computedHref}
      rel={rel}
      title={title}
      onClick={handleClick}
    >
      {children}
    </a>
  )
  // 原窗口跳转时，使用next link 包裹
  if (target === '_self' && computedHref) {
    return <Link href={computedHref}>{anchor}</Link>
  }
  return anchor
}
